import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {NavigationService} from '../services/navigation/navigation.service';
import {TvShopEntertainmentPack} from '../model/tv-shop-entertainment-pack';
import {LabelkeyService} from '../services/labelkey/labelkey.service';
import {SelectableNavigationItem} from '../model/selectable-navigation-item';
import {Order} from '../model/order';
import {OmapiIdConstants} from '../shared/omapi-id-constants';
import {EmitterConstants} from '../shared/emitter-constants';
import {Emitter} from '../shared/utils/emitter/emitter';
import {AnalyticsService} from '../services/analytics/analytics.service';
import {TvShopStateService} from '../services/state/tv-shop-state.service';

@Component({
  selector: 'app-order-button',
  templateUrl: './order-button.component.html',
  styleUrls: ['./order-button.component.less'],
  providers: [LabelkeyService]
})
export class OrderButtonComponent extends SelectableNavigationItem implements OnInit, OnChanges {

  @Input() pack: TvShopEntertainmentPack;
  @Input() subPack: TvShopEntertainmentPack;
  @Input() active: boolean;
  @Input() buttonTextKey: string;
  @Input() isDeepLinking: boolean;
  @Input() isOrderable = true;
  @Input() isGroupPack = false;
  @Input() isDayPass: boolean;
  @Input() isTrial: boolean;
  @Input() superiorSubscribedPack: TvShopEntertainmentPack;
  @Input() isPlayAndInfoPage: boolean;

  @Output() emitOnEnterPressed = new EventEmitter<Order>();
  @Output() emitOnBack = new EventEmitter<Object>();

  orderButtonAvailable: boolean;
  downGradePackIsTrial = false;

  constructor(private navigationService: NavigationService,
              private labelkeyService: LabelkeyService,
              private stateService: TvShopStateService,
              private analyticsService: AnalyticsService,
              private cd: ChangeDetectorRef) {
    super();
    Emitter.of(EmitterConstants.DOWNGRADE_PACK_IS_TRIAL).subscribe(value => {
      if (value) {
        this.downGradePackIsTrial = value;
      }
    });
  }

  @Input()
  onEnter() {
    let order: Order;
    if (((this.isDeepLinking) && !this.isOrderable || this.orderButtonAvailable) && this.isPackOrderable() ||
      this.isTrial ||
      this.isGroupPack) {
      order = this.createOrder();
      this.emitOnEnterPressed.emit(order);
      if (order.type !== Order.TRIALS) {
        this.analyticsService.onReadyToOrder(order);
      }
    }
  }

  ngOnInit() {
    this.stateService.isOrderProcessPending$.subscribe(orderProcessPending => {
      this.orderButtonAvailable = !orderProcessPending || !this.isOrderable;
      this.determineButtonText();
    });
    this.registerOnNavigationService();
  }

  registerOnNavigationService(): void {
    this.navigationService.registerNavigation(this, this.ownerComponent);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.determineButtonText();
  }

  public get activeGroupVariant() {
      return this.active;
  }
  public get activeClass() {
    return this.active && (!this.subPack && !this.isPackSubscribed || this.subPack && !this.isSubPackSubscribed);
  }

  activate(active: boolean): void {
    this.active = active;
    this.cd.detectChanges();
  }

  public determineButtonText(): void {
    if (this.buttonTextKey !== undefined) {
      return;
    }
    if (this.subPack === undefined) {
      this.buttonTextKey = this.getButtonTextForPack(this.pack);
    } else {
      if (!this.pack.subscribed && !this.subPack.subscribed) {
        this.buttonTextKey = this.labelkeyService.getOrderButtonLabelKeyWithSubProduct(this.pack.omapiId, this.subPack.omapiId);
      } else {
        this.buttonTextKey = this.getButtonTextForPack(this.subPack);
      }
    }
  }

  private getButtonTextForPack(pack: TvShopEntertainmentPack): string {
    if (pack.subscribed) {
      return this.labelkeyService.getSubscribedLabelKey(pack.omapiId, this.isDeepLinking);
    }
    if (this.shouldShowUpgrade(pack)) {
      return this.labelkeyService.getLabelForUpgrade();
    }
    return this.labelkeyService.getOrderButtonLabelKey(pack.omapiId);
  }

  private shouldShowUpgrade(pack: TvShopEntertainmentPack): boolean {
    return pack && pack.isUpgrade && this.isOrderable;
  }

  private isPackOrderable() {
    if (this.pack && this.subPack) {
      if (this.pack.subscribed) {
        return this.subPack.isOrderable();
      } else {
        return this.pack.isOrderable() && this.subPack.isOrderable();
      }
    } else if (this.pack) {
      return this.pack.isOrderable();
    }

    return false;
  }

  private createOrder() {
    const omapiId: string = this.isTrial ? OmapiIdConstants.TRIALS : this.pack.omapiId;
    const subOmapiId: string = this.subPack ? this.subPack.omapiId : undefined;
    const packPrice: number = this.pack && this.pack.priceDetail && this.pack.priceDetail.promoPrice ?
      this.pack.priceDetail.promoPrice.value : undefined;
    const subPackPrice: number = this.subPack && this.subPack.priceDetail && this.subPack.priceDetail.promoPrice ?
      this.subPack.priceDetail.promoPrice.value : undefined;
    const orderType = this.isDayPass ? Order.DAYPASS : this.isTrial ? Order.TRIALS : Order.ORDER;
    const order = new Order(omapiId, packPrice, subOmapiId, subPackPrice, orderType);
    if (this.pack && !this.pack.isOrderable() && this.subPack && this.subPack.isOrderable()) {
      this.transformSubpackToPack(order);
    }
    return order;
  }

  get isPackSubscribed(): boolean {
    return this.pack && this.pack.subscribed;
  }

  get isDowngrade(): boolean {
    return this.pack && this.pack.isDowngrade;
  }

  get isSubPackSubscribed(): boolean {
    return this.subPack && this.subPack.subscribed;
  }

  get isPlayMoreAndUpgradeFromTrial(): boolean {
    return this.pack && (this.pack.omapiId === OmapiIdConstants.PLAY_MORE) && this.downGradePackIsTrial;
  }

  public get isPlay(): boolean {
    return this.pack && this.pack.omapiId === OmapiIdConstants.STREAMZ || this.pack && this.pack.omapiId === OmapiIdConstants.RED_CARPET_MORE;
  }

  public packName(): string {
    if (this.pack && this.pack.omapiId) {
      if (this.pack.omapiId === OmapiIdConstants.RED_CARPET_MORE) {
        return OmapiIdConstants.COM_NAME_RED_CARPET_MORE;
      }
      return OmapiIdConstants.PRODUCT_NAME_MAP.get(this.pack.omapiId);
    }
    return '';
  }

  public superiorSubscribedPackName(): string {
    if (this.superiorSubscribedPack) {
      return this.superiorSubscribedPack.omapiId === OmapiIdConstants.RED_CARPET_MORE ?
        OmapiIdConstants.COM_NAME_RED_CARPET_MORE : OmapiIdConstants.PRODUCT_NAME_MAP.get(this.superiorSubscribedPack.omapiId);
    }
    return '';
  }

  get showOrderButton(): boolean {
    return (this.orderButtonAvailable || this.isPackSubscribed) && !this.isDowngrade;
  }

  get showPendingOrderInfo(): boolean {
    return !this.orderButtonAvailable && !this.isPackSubscribed;
  }

  get showDowngradeMessage(): boolean {
    return this.orderButtonAvailable && this.isDowngrade;
  }

  onBack(): void {
    this.emitOnBack.emit({
      value: true
    });
  }

  public isSubscribed(): boolean {
    return this.isSubscribedToPackOrSubPack;
  }

  get isSubscribedToPackOrSubPack(): boolean {
    return (this.isPackSubscribed && !this.subPack) || (this.isPackSubscribed && this.isSubPackSubscribed);
  }

  canNavigateToDirection(keyCode: number): boolean {
    return this.isDeepLinking && this.pack && !this.pack.subscribed || !this.isDeepLinking;
  }

  private transformSubpackToPack(event: Order) {
    event.packOmapiId = event.subPackOmapiId;
    event.packPrice = event.subPackPrice;
    event.subPackOmapiId = undefined;
    event.subPackPrice = undefined;
  }
}
